<script setup>

import Shared from '@/common/shared'
import MainFooter from '@/components/partial/MainFooter.vue'
import {computed, ref} from 'vue'
import {Dictionary, Funds, MySelf, Regions, Rgo} from '@/common/api.service'
import {useRoute, useRouter} from 'vue-router'
import ValidateInput from '@/components/validate/input.vue'
import moment from 'moment'
import {useToast} from 'vue-toastification'
import {OLK_URL} from "@/common/config";

const route = useRoute()
const router = useRouter()
const toast = useToast()
const olkUrl = computed(() => OLK_URL.endsWith('/') ? OLK_URL : OLK_URL + '/')
const item = ref({
  tranches: [],
  targets: null,
})
const ownPrograms = ref([])
const region = ref({})
const fund = ref({})
const isLoading = ref(false)
const counterparties = ref([])
const counterparty = ref([])
const supportedIndustries = ref([])
const frpIndustries = ref([])
const loanTargets = ref([])
const dbl = ref(0)
const itemFields = {
  agreementNumber: ref(null),
  counterparty: ref(null),
  projectTitle: ref(null),
  program: ref(null),
  targetDescription: ref(null),
  okved: ref(null),
  okvedCustom: ref(null),
  applicationSubmittingDate: ref(null),
  agreementDate: ref(null),
  fundsTransferDate: ref(null),
  comment: ref(null),
  loanSum: ref(null),
  cofinanceSum: ref(null),
  totalBudgetOfProject: ref(null),
  summaryGainOfProject: ref(null),
  summaryTaxRevenue: ref(null),
  applicationsForIntellectualPropertyObjectQuantity: ref(null),
  highPerformanceWorkbenchQuantity: ref(null),
  bankGuarantee: ref(null),
  legalEntityGuarantee: ref(null),
  individualGuarantee: ref(null),
  mspCorporationGuarantee: ref(null),
  rgoGuarantee: ref(null),
  realEstateGuarantee: ref(null),
  equipmentAndTransportGuarantee: ref(null),
  securitiesAndOtherGuarantee: ref(null),
  frpIndustry: ref(null),
  frpIndustryCustom: ref(null),
  targets: ref(null),
}
const idCurrentProgramm = ref(0)

const permissions = ref([])
const isRegionalFundEmployee = ref(false)
await MySelf.card().then(async ({data}) => {
  isRegionalFundEmployee.value = data.isRegionalFundEmployee
})

await Regions.permissions()
    .then(async ({data}) => {
      permissions.value = data
    }).catch(async () => {
      await router.push({name: 'Error'})
    })

function permission(permission) {
  return permissions.value.find((p) => p === permission)
}

const isEditForm = ref(permission('/own-programs/agreements:create') || permission('/own-programs/agreements:update'))

const counterpartyList = computed(() => {
  let r = []
  counterparties.value.forEach(function (f) {
    r.push({value: f.value, label: `${f.label}`})
  })
  return r
})
const supportedIndustriesList = computed(() => {
  let r = []
  supportedIndustries.value.forEach(function (f) {
    r.push({value: f.value, label: `${f.title}`})
  })

  return r
})
const frpIndustriesList = computed(() => {
  let r = []
  frpIndustries.value.forEach(function (f) {
    r.push({value: f.value, label: `${f.title}`})
  })

  return r
})
const targetList = computed(() => {
  let r = []
  loanTargets.value.forEach(function (f) {
    r.push({value: f.value, label: `${f.title}`})
  })

  return r
})
const otherTarget = computed(() => {
  if (item.value.targets === null) {
    return false
  }
  return item.value.targets.find(v => v === 1)
})

async function loadItem(programId, id) {
  await Funds.ownProgramAgreement(programId, id)
      .then(async ({data}) => {
        item.value = {
          agreementNumber: data.agreementNumber,
          counterparty: data.counterpartyId,
          projectTitle: data.projectTitle,
          program: data.program.id,
          targetDescription: data.targetDescription,
          okved: data.okved,
          okvedCustom: data.okvedCustom,
          applicationSubmittingDate: data.applicationSubmittingDate,
          agreementDate: data.agreementDate,
          fundsTransferDate: moment(data.fundsTransferDate).format('YYYY-MM-DD'),
          comment: data.comment,
          loanSum: data.loanSum,
          cofinanceSum: data.cofinanceSum,
          totalBudgetOfProject: data.totalBudgetOfProject,
          summaryGainOfProject: data.summaryGainOfProject,
          summaryTaxRevenue: data.summaryTaxRevenue,
          applicationsForIntellectualPropertyObjectQuantity: data.applicationsForIntellectualPropertyObjectQuantity,
          highPerformanceWorkbenchQuantity: data.highPerformanceWorkbenchQuantity,
          bankGuarantee: data.bankGuarantee,
          legalEntityGuarantee: data.legalEntityGuarantee,
          individualGuarantee: data.individualGuarantee,
          mspCorporationGuarantee: data.mspCorporationGuarantee,
          rgoGuarantee: data.rgoGuarantee,
          realEstateGuarantee: data.realEstateGuarantee,
          equipmentAndTransportGuarantee: data.equipmentAndTransportGuarantee,
          securitiesAndOtherGuarantee: data.securitiesAndOtherGuarantee,
          frpIndustry: data.frpIndustry,
          frpIndustryCustom: data.frpIndustryCustom,
          tranches: data.tranches,
          targets: data.targets,
        }
        idCurrentProgramm.value = data.program.id
        if (data.organizationTitle && item.value.counterparty) {
          await Rgo.counterparties(data.organizationTitle).then(({data}) => {
            data.forEach(function (f) {
              counterparties.value.push({value: {"value":f}, label: `${f.title} ИНН: ${f.inn} ОГРН: ${f.ogrn}`})
              if (item.value.counterparty === f.value) {
                counterparty.value = {value: f, label: `${f.title} ИНН: ${f.inn} ОГРН: ${f.ogrn}`}
              }
            })
          })

          if (counterparties.value.length === 0) {
            counterparties.value.push({value: {"value":item.value.counterparty}, label: `${data.organizationTitle}`})
            counterparty.value = {value: {"value":item.value.counterparty}, label: `${data.organizationTitle}`}
          }
        }else if (!data.organizationTitle && item.value.counterparty){
          counterparties.value.push({value: {"value": item.value.counterparty}, label: item.value.counterparty})
          counterparty.value = {value: item.value.counterparty, label: item.value.counterparty}
        }
      }).catch(async (data) => {
        if (data.response && data.response.status === 403) {
          await router.push({name: 'ForbiddenPage'})
        }

        if (data.response && data.response.status === 404) {
          toast.error(data.response.data.title, {
            timeout: 5000
          })
        }
      })
}

await Regions.item(route.params.id)
    .then(({data}) => {
      window.scrollTo(0,0);
      region.value = data
    }).catch(async (data) => {
      if (data.response && data.response.status === 403) {
        await router.push({name: 'ForbiddenPage'})
      }
    })
await Funds.item(region.value.fund.id)
    .then(({data}) => {
      fund.value = data
    }).catch(() => {
    })

await Funds.ownPrograms(region.value.fund.id)
    .then(({data}) => {
      ownPrograms.value = data
      if (data.length > 0 && !item.value.program) {
        item.value.program = data[0].id
      }
    }).catch(() => {
    })

await Dictionary.supportedIndustries()
    .then(({data}) => {
      supportedIndustries.value = data
      if (data.length > 0) {
        item.value.okved = data[0].value
      }
    }).catch(() => {
    })

await Dictionary.frpIndustries()
    .then(({data}) => {
      frpIndustries.value = data
    }).catch(() => {
    })

await Dictionary.loanTargets()
    .then(({data}) => {
      loanTargets.value = data
    }).catch(() => {
    })

if (route.params.agreement) {
  await loadItem(route.params.program, route.params.agreement)
}

async function counterpartySearch(query) {
  isLoading.value = true
  counterparties.value = []
  if (query === '') {
    isLoading.value = false
    return
  }
  let countReturnTemp = 0;
  await Rgo.counterparties(query).then(({data}) => {
    data.forEach(function (f) {
      let label = `${f.title} ИНН: ${f.inn} ОГРН: ${f.ogrn}`
      let tempRecord = counterparties.value.filter(r => r.label === label)
      if (tempRecord.length == 0 && countReturnTemp <=19) {
        counterparties.value.push({value: f, label: label})
        countReturnTemp++
      }
    })
  })
  isLoading.value = false
}

async function showRemove() {
  Shared.showModal('#del-document')
}

async function closeRemove() {
  Shared.closeModal('#del-document')
}

async function remove() {
  if (route.params.agreement) {
    await Funds.ownProgramsDeleteAgreement(item.value.program, route.params.agreement).then(async () => {
      await router.push({name: 'ListAgreements', query: {region: route.params.id}})
    })
  }
  Shared.showModal('#del-document')
}

async function saveOwnProgram() {
  let isValid = true
  /*if (item.value.tranches.length === 0) {
    isValid = false
  }*/

  if (item.value.tranches.length > 0) {
    let tempTransh = []
    item.value.tranches.forEach((f) => {
      if (Object.keys(f).length > 0) {
        if ( f.sum === undefined || f.date === undefined){
          isValid = false
        }
        tempTransh.push(f)
      }
    })

    item.value.tranches = tempTransh
  }

  Object.keys(itemFields).forEach((f) => {
    if (f !== 'targetDescription') {
      if (itemFields[f].value) {
        itemFields[f].value.validate()
        if (itemFields[f].value.validationFailed) {
          isValid = false
        }
      }
    } else {
      if (otherTarget.value) {
        itemFields[f].value.validate()
        if (itemFields[f].value.validationFailed) {
          isValid = false
        }
      }
    }
  })
  if (!item.value.targets || item.value.targets.length === 0) {
    isValid = false
  }

  if (isValid) {
    if (route.params.agreement) {
      if (counterparty.value && counterparty.value.value.value && counterparty.value.value.value.value !== ''){
        item.value.counterpartyId = counterparty.value.value.value
      }else if (counterparty.value && counterparty.value.value !== ''){
        item.value.counterpartyId = counterparty.value.value
      }else if (item.value.counterparty){
        item.value.counterpartyId = item.value.counterparty
        delete item.value.counterparty
      }

      if (idCurrentProgramm.value !== 0 &&
          item.value.program !== '' &&
          idCurrentProgramm.value !== item.value.program) {
        item.value.targetProgramId = item.value.program
      }else{
        item.value.targetProgramId = null
      }

      await Funds.ownProgramsUpdateAgreement((idCurrentProgramm.value)?idCurrentProgramm.value:item.value.program, route.params.agreement, item.value).then(async () => {
        await router.push({name: 'ListAgreements', query: {region: route.params.id}})
      }).catch((response) => {
        dbl.value++
        toast.error(response.response.data.title, {
          timeout: 5000
        })
      })
    } else {
      item.value.counterpartyId = counterparty.value.value
      //item.value.okved = String(item.value.okved)
      await Funds.ownProgramsCreateAgreement(item.value.program, item.value).then(async () => {
        await router.push({name: 'ListAgreements', query: {region: route.params.id}})
      }).catch((response) => {
        toast.error(response.response.data.title, {
          timeout: 5000
        })
      })
    }
  } else {
    dbl.value++
    toast.error('Договор не сохранен, проверьте обязательные поля и траншевое финансирование', {
      timeout: 5000
    })
  }
}

</script>

<template>
  <div class="wrapper">
    <MainHeader></MainHeader>
    <main class="page page--js">
      <div class="breadcrumbs-container">
        <div class="container">
          <div class="breadcrumbs-wrapper">
            <ul class="breadcrumbs">
              <li>
                Модуль регионов
              </li>
              <li v-if="!isRegionalFundEmployee">
                <router-link :to="{ name: 'ListRegions' }">Список регионов
                </router-link>
              </li>
              <li v-else>Список регионов</li>
              <li>
                <router-link :to="{ name: 'CardRegions', params: { id: region.id } }">{{ fund.region.title }}
                </router-link>
              </li>
              <li>
                <router-link :to="{ name: 'ListAgreements', query: { region: region.id } }">Реестр договоров займа
                  по самостоятельным программам
                </router-link>
              </li>
              <li v-if="!route.params.agreement">Добавление договора займа
              </li>
              <li v-else>Редактирование договора займа
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div class="content" v-if="permission('/own-programs/agreements:read')">
        <div class="container">
          <div class="content__wrapper">
            <div class="content__contract">
              <div class="content__title content__title--contract">
                <h1 class="title title-h1--contract"
                    v-if="!route.params.agreement && permission('/own-programs/agreements:create')">Добавление договора займа</h1>
                <h1 class="title title-h1--contract" v-else-if="permission('/own-programs/agreements:update')">Редактирование договора
                  займа</h1>
                <h1 class="title title-h1--contract" v-else>Просмотр договора займа</h1>
              </div>
              <form action="#" class="contract">
                <div class="contract__row">
                  <div class="contract__item">
                    <ValidateInput
                        :name="'Номер договора займа'"
                        placeholder="Введите номер договора займа"
                        :ref="itemFields.agreementNumber"
                        required
                        :disabled="!isEditForm"
                        v-model="item.agreementNumber">
                    </ValidateInput>
                    <ValidateInput
                        label-class="title-field title-field--relative"
                        object-validation-key="value"
                        :name="'Название организации'"
                        :ref="itemFields.counterparty"
                        required
                        v-model="counterparty">
                      <template v-slot:field="{validationFailed}">
                        <Multiselect class="field field--select c-input c-select c-filter__input c-multiselect-filter"
                                     :class="{unvalidate: validationFailed}"
                                     v-model="counterparty"
                                     mode="single"
                                     :loading="isLoading"
                                     @search-change="counterpartySearch"
                                     :min-chars="2"
                                     :show-no-results="false"
                                     :searchable="true"
                                     :options="counterpartyList"
                                     :disabled="!isEditForm"
                                     placeholder="Название или ИНН организации"
                                     :no-options-text="'Укажите ОГРН или ИНН'"/>
                        <a
                            v-if="isEditForm"
                            :href="olkUrl+'counterparty'"
                            class="field--add-icon-green link-select-item"
                           target="_blank"></a>
                      </template>
                    </ValidateInput>
                    <ValidateInput
                        :name="'Наименование проекта'"
                        placeholder="Введите наименование проекта"
                        :ref="itemFields.projectTitle"
                        required
                        textarea
                        :disabled="!isEditForm"
                        v-model="item.projectTitle">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Программа РФРП'"
                        required
                        :ref="itemFields.program"
                        select
                        :items="ownPrograms"
                        :item-key="'id'"
                        :item-name="'title'"
                        :disabled="!isEditForm"
                        v-model="item.program">
                    </ValidateInput>
                    <ValidateInput
                        label-class="title-field title-field--relative"
                        :name="'На какие цели предоставлен займ'"
                        :ref="itemFields.targets"
                        required
                        nonEmptyArray
                        v-model="item.targets">
                      <template v-slot:field="{validationFailed}">
                        <Multiselect placeholder="" class="field field--select"
                                     :class="{unvalidate: validationFailed}"
                                     style="height: auto" v-model="item.targets"
                                     mode="tags" :searchable="true" :aria-expanded="true"
                                     :disabled="!isEditForm"
                                     :options="targetList"/>
                      </template>
                    </ValidateInput>
                    <ValidateInput
                        :name="''"
                        placeholder="Комментарий"
                        :ref="itemFields.targetDescription"
                        v-if="otherTarget"
                        required
                        :disabled="!isEditForm"
                        v-model="item.targetDescription">
                    </ValidateInput>
                    <ValidateInput
                        label-class="title-field title-field--relative"
                        :name="'ОКВЭД проекта'"
                        :ref="itemFields.okved"
                        required
                        v-model="item.okved">
                      <template v-slot:field="{validationFailed}">
                        <Multiselect placeholder="" class="field field--select"
                                     :class="{unvalidate: validationFailed}"
                                     style="height: auto" v-model="item.okved"
                                     :disabled="!isEditForm"
                                     mode="single" :searchable="true" :aria-expanded="true"
                                     :options="supportedIndustriesList"/>
                        <ValidateInput
                            placeholder="Введите комментарий по ОКВЭД"
                            :ref="itemFields.okvedCustom"
                            v-if="supportedIndustriesList && item.okved && item.okved === supportedIndustriesList.find(l => l.label === 'Иное').value"
                            required
                            maxlength="255"
                            v-model="item.okvedCustom">
                        </ValidateInput>
                      </template>
                    </ValidateInput>
                    <ValidateInput
                        label-class="title-field title-field--relative"
                        :name="'Отрасль промышленности'"
                        :ref="itemFields.frpIndustry"
                        required
                        v-model="item.frpIndustry">
                      <template v-slot:field="{validationFailed}">
                        <Multiselect placeholder="" class="field field--select"
                                     :class="{unvalidate: validationFailed}"
                                     style="height: auto" v-model="item.frpIndustry"
                                     :disabled="!isEditForm"
                                     mode="single" :searchable="true" :aria-expanded="true"
                                     :options="frpIndustriesList"/>
                        <ValidateInput
                            placeholder="Введите название отрасли промышленности"
                            :ref="itemFields.frpIndustryCustom"
                            v-if="frpIndustriesList && item.frpIndustry && item.frpIndustry === frpIndustriesList.find(l => l.label === 'Иное').value"
                            required
                            maxlength="255"
                            v-model="item.frpIndustryCustom">
                        </ValidateInput>
                      </template>
                    </ValidateInput>
                    <ValidateInput
                        label-class="title-field title-field--relative"
                        :name="'Дата подачи заявки'"
                        :ref="itemFields.applicationSubmittingDate"
                        required
                        v-model="item.applicationSubmittingDate">
                      <template v-slot:field="{validationFailed}">
                        <VueDatePicker model-type="yyyy-MM-dd" placeholder="00.00.0000" class="field field--calendar"
                                       :class="{unvalidate: validationFailed}"
                                       :disabled="!isEditForm"
                                       v-model="item.applicationSubmittingDate"/>
                      </template>
                    </ValidateInput>
                    <ValidateInput
                        label-class="title-field title-field--relative"
                        :name="'Дата договора займа'"
                        :ref="itemFields.agreementDate"
                        required
                        v-model="item.agreementDate">
                      <template v-slot:field="{validationFailed}">
                        <VueDatePicker model-type="yyyy-MM-dd" placeholder="00.00.0000" class="field field--calendar"
                                       :class="{unvalidate: validationFailed}"
                                       :disabled="!isEditForm"
                                       v-model="item.agreementDate"/>
                      </template>
                    </ValidateInput>
                    <ValidateInput
                        label-class="title-field title-field--relative"
                        :name="'Дата перечисления средств'"
                        :ref="itemFields.fundsTransferDate"
                        required
                        v-model="item.fundsTransferDate">
                      <template v-slot:field="{validationFailed}">
                        <VueDatePicker model-type="yyyy-MM-dd" placeholder="00.00.0000" class="field field--calendar"
                                       :class="{unvalidate: validationFailed}"
                                       :disabled="!isEditForm"
                                       v-model="item.fundsTransferDate"/>
                      </template>
                    </ValidateInput>
                    <ValidateInput
                        :name="'Комментарий'"
                        placeholder="Введите комментарий"
                        :ref="itemFields.comment"
                        textarea
                        :disabled="!isEditForm"
                        v-model="item.comment">
                    </ValidateInput>
                  </div>
                  <div class="contract__item">
                    <ValidateInput
                        :name="'Сумма займа, руб.'"
                        placeholder="Введите cумму займа"
                        :ref="itemFields.loanSum"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.loanSum">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Софинансирование (частные инвестиции), руб.'"
                        placeholder="Введите софинансирование в руб."
                        :ref="itemFields.cofinanceSum"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.cofinanceSum">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Общий бюджет проекта, руб.'"
                        placeholder="Введите бюджет проекта в руб."
                        :ref="itemFields.totalBudgetOfProject"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.totalBudgetOfProject">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Суммарный объем выручки по проекту, руб.'"
                        placeholder="Введите суммарный объем выручки в руб."
                        :ref="itemFields.summaryGainOfProject"
                        input-class="field"
                        :float-format="2"
                        required
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.summaryGainOfProject">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Объем налоговых поступлений, руб.'"
                        placeholder="Введите объем налоговых поступлений в руб."
                        :ref="itemFields.summaryTaxRevenue"
                        input-class="field"
                        :float-format="2"
                        required
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.summaryTaxRevenue">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Кол-во заявок на объекты интеллектуальной собственности, шт.'"
                        placeholder="Введите количество заявок"
                        :ref="itemFields.applicationsForIntellectualPropertyObjectQuantity"
                        input-class="field"
                        rounded
                        required
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.applicationsForIntellectualPropertyObjectQuantity">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Кол-во высокопроизв. рабочих мест, шт.'"
                        placeholder="Введите количество рабочих мест"
                        :ref="itemFields.highPerformanceWorkbenchQuantity"
                        input-class="field"
                        rounded
                        required
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.highPerformanceWorkbenchQuantity">
                    </ValidateInput>
                  </div>
                </div>
                <div class="title title--contract-subtitle">Обеспечение</div>
                <div class="contract__row">
                  <div class="contract__item">
                    <ValidateInput
                        :name="'Банковская гарантия, руб.'"
                        placeholder="Введите значение в руб."
                        :ref="itemFields.bankGuarantee"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.bankGuarantee">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Поручительство ЮЛ, руб.'"
                        placeholder="Введите значение в руб."
                        :ref="itemFields.legalEntityGuarantee"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.legalEntityGuarantee">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Поручительство физ. лиц, руб.'"
                        placeholder="Введите значение в руб."
                        :ref="itemFields.individualGuarantee"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.individualGuarantee">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Поручительство корпорации МСП, руб.'"
                        placeholder="Введите значение в руб."
                        :ref="itemFields.mspCorporationGuarantee"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.mspCorporationGuarantee">
                    </ValidateInput>
                  </div>
                  <div class="contract__item">
                    <ValidateInput
                        :name="'РГО, руб.'"
                        placeholder="Введите значение в руб."
                        :ref="itemFields.rgoGuarantee"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.rgoGuarantee">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Недвижимость, руб.'"
                        placeholder="Введите значение в руб."
                        :ref="itemFields.realEstateGuarantee"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.realEstateGuarantee">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Оборудование и транспорт, руб.'"
                        placeholder="Введите значение в руб."
                        :ref="itemFields.equipmentAndTransportGuarantee"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.equipmentAndTransportGuarantee">
                    </ValidateInput>
                    <ValidateInput
                        :name="'Ценные бумаги и иное, руб.'"
                        placeholder="Введите значение в руб."
                        :ref="itemFields.securitiesAndOtherGuarantee"
                        input-class="field"
                        required
                        :float-format="2"
                        number
                        can-set-null
                        :disabled="!isEditForm"
                        v-model="item.securitiesAndOtherGuarantee">
                    </ValidateInput>
                  </div>
                </div>
                <div class="tranche-financing">
                  <div class="title title--tranche-financing">Финансирование проекта (в т.ч. транши)
                    <button type="button"
                            class="button-add" @click="item.tranches.push({})"></button>
                  </div>
                  <div class="table-calendar-wrapper table-container--pad-inline">
                    <table class="table table--table-mini">
                      <thead>
                      <tr>
                        <th>Дата</th>
                        <th>Сумма, руб.</th>
                        <th></th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="(i, index) in item.tranches" :key="i">
                        <td class="table-calendar-td">
                          <VueDatePicker model-type="yyyy-MM-dd" format="dd.MM.yyyy" placeholder="00.00.0000"
                                         :disabled="!isEditForm"
                                         class="field field--calendar field--calendar-static" v-model="i.date"/>
                        </td>
                        <td>
                          <NumberInput v-model="i.sum" input-class="field"
                                       :float-format="2"
                                       :disabled="!isEditForm"
                                       placeholder="Введите сумму займа"/>
                        </td>
                        <td>
                          <div class="table-button-block">
                            <button type="button"
                                    v-if="isEditForm"
                                    @click="item.tranches.splice(index, 1)" class="button-del"></button>
                          </div>
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="buttons-block buttons-block--right">
                  <button
                      type="button"
                      class="button button--delete"
                      v-if="route.params.agreement && permission('/own-programs/agreements:delete')"
                      @click="showRemove">
                    Удалить
                  </button>

                  <router-link class="button button--clear"
                               :to="{ name: 'ListAgreements', query: { region: region.id } }">{{ (isEditForm)?'Отмена изменений':'Закрыть' }}
                  </router-link>
                  <button type="button"
                          class="button"
                          v-if="isEditForm"
                          :key="dbl"
                          @click.once="saveOwnProgram">сохранить</button>
                </div>
              </form>
              <div>
                Поля, помеченные знаком «*», являются обязательными для заполнения.<br/>
                Если в каком-то числовом поле не предусмотрено значения, укажите 0.
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-overlay" id="del-document">
        <div class="popup popup-del">
          <div class="modal-title">Удаление записи</div>
          <div class="modal-body">Вы действительно хотите удалить договор займа?</div>
          <div class="modal-bottom button-dialog-center">
            <div class="buttons-block">
              <button type="button" class="button button--clear close-modal--js" @click="closeRemove">Закрыть</button>
              <button type="button"
                      class="button button--delete sub-modal-open--js"
                      data-modal="close-document"
                      @click="remove">Удалить
              </button>
            </div>
          </div>
        </div>
      </div>
      <div class="modal-overlay" id="close-document">
        <div class="popup">
          <div class="modal-title">Запись удалена</div>
          <div class="modal-body"></div>
          <div class="modal-bottom">
            <div class="buttons-block">
              <button type="button" class="button close-modal--js">Закрыть</button>
            </div>
          </div>
        </div>
      </div>
    </main>
    <MainFooter></MainFooter>
  </div>
</template>

<style scoped></style>